/**
 * Settings tab → Alerts: the engine's push rules for the Signal Predictor
 * phone app (GET/PUT /api/flow-alerts/settings). Only the production engine
 * sends; the same rules gate this browser's chimes.
 */
import React, { useEffect, useState } from 'react';
import { useFlowAlertsStore } from '../../store/flowAlertsStore';
import { Switch } from './SignalsTab';
import {
  ALERT_KINDS,
  DEFAULT_ALERT_SETTINGS,
  kindName,
  tfGlyph,
  TF_ROOTS,
  type FlowAlertSettings,
} from './tradeFinderUx';

const AlertsSection: React.FC = () => {
  const saved = useFlowAlertsStore((s) => s.settings);
  const status = useFlowAlertsStore((s) => s.status);
  const loadError = useFlowAlertsStore((s) => s.error);
  const [draft, setDraft] = useState<FlowAlertSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    void useFlowAlertsStore.getState().fetch();
    return useFlowAlertsStore.getState().listen();
  }, []);

  // A fresh server copy replaces the draft only while nothing is being edited.
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);
  const s: FlowAlertSettings = draft ?? saved ?? DEFAULT_ALERT_SETTINGS;
  const edit = (patch: Partial<FlowAlertSettings>) => {
    setMsg(null);
    setDraft({ ...s, ...patch });
  };

  const save = async () => {
    setSaving(true);
    const r = await useFlowAlertsStore.getState().save(s);
    setSaving(false);
    if (r.ok) {
      setDraft(null);
      setMsg({ ok: true, text: 'Saved' });
    } else {
      setMsg({ ok: false, text: `Not saved: ${r.error}` });
    }
  };

  const num = (v: string, def: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  const summary = s.enabled
    ? `${ALERT_KINDS.filter((k) => s.kinds[k]).map(kindName).join(', ') || 'no kinds'} · ${s.symbols.join('/') || 'no symbols'}`
      + `${s.atLevelOnly ? ' · at a level' : ''} · |t| ≥ ${s.minEdgeT}`
    : 'off';

  return (
    <div className={`tf-alerts tf-set-group ${open ? 'is-open' : ''}`} data-testid="tradefinder-alerts">
      <button
        type="button"
        className="tf-set-group-head tf-alerts-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="tradefinder-alerts-toggle"
      >
        <span className="tf-alerts-title">{open ? '▾' : '▸'} Alerts (Signal Predictor push){dirty ? ' •' : ''}</span>
        <span className="tf-settings-muted tf-alerts-summary" data-testid="tradefinder-alerts-summary">{summary}</span>
      </button>
      {open && renderBody()}
    </div>
  );

  function renderBody() {
    return (
    <div className="tf-alerts-body">
      {status === 'error' && !saved && (
        <div className="tf-settings-banner is-error" data-testid="tradefinder-alerts-error">
          Alert settings unavailable ({loadError ?? 'engine not reachable'}). Showing defaults.
        </div>
      )}
      <div className="tf-settings-muted tf-alerts-note">
        Sent by the production engine to the phone app's "TradeFinder" channel; the browser chimes use the same rules.
      </div>
      <Switch
        on={s.enabled}
        label="Push alerts"
        description="Master switch for TradeFinder push notifications"
        glyph="🔔"
        testId="tradefinder-alerts-enabled"
        onChange={(on) => edit({ enabled: on })}
      />
      <div className="levels-group-heading">Kinds</div>
      {ALERT_KINDS.map((k) => (
        <Switch
          key={k}
          on={s.kinds[k]}
          label={kindName(k)}
          glyph={tfGlyph(k)}
          muted={!s.enabled}
          testId={`tradefinder-alerts-kind-${k}`}
          onChange={(on) => edit({ kinds: { ...s.kinds, [k]: on } })}
        />
      ))}
      <div className="levels-group-heading">Filters</div>
      <div className="tf-ux-row">
        <span>Symbols</span>
        <span className="tf-alerts-chips">
          {TF_ROOTS.map((r) => {
            const on = s.symbols.includes(r);
            return (
              <button
                key={r}
                type="button"
                className={`tf-alerts-chip ${on ? 'is-on' : ''}`}
                aria-pressed={on}
                data-testid={`tradefinder-alerts-symbol-${r}`}
                onClick={() => edit({ symbols: on ? s.symbols.filter((x) => x !== r) : [...s.symbols, r] })}
              >
                {r}
              </button>
            );
          })}
        </span>
      </div>
      <label className="tf-ux-row">
        <span>Side</span>
        <select value={s.side} onChange={(e) => edit({ side: e.target.value as FlowAlertSettings['side'] })} data-testid="tradefinder-alerts-side">
          <option value="both">Buy and sell</option>
          <option value="buy">Buy only</option>
          <option value="sell">Sell only</option>
        </select>
      </label>
      <label className="tf-ux-row">
        <span>Min edge |t|</span>
        <input
          type="number" min={0} max={10} step={0.1} value={s.minEdgeT}
          onChange={(e) => edit({ minEdgeT: num(e.target.value, s.minEdgeT) })}
          data-testid="tradefinder-alerts-min-edge"
        />
      </label>
      <Switch
        on={s.allowMissingEdge}
        label="Alert when edge data is missing"
        description="Off: a signal without a tuner reading never alerts"
        glyph="?"
        testId="tradefinder-alerts-allow-missing-edge"
        onChange={(on) => edit({ allowMissingEdge: on })}
      />
      <Switch
        on={s.atLevelOnly}
        label="Only at a key level"
        description="Within the tolerance below of PW, CW, ZG, IV±, POC, VAH, VAL, wVAH, wVAL, ONH, ONL, PDH, PDL or MOC"
        glyph="◎"
        testId="tradefinder-alerts-at-level"
        onChange={(on) => edit({ atLevelOnly: on })}
      />
      <div className="tf-ux-row tf-alerts-tol">
        <span>Level tolerance (pts)</span>
        <span className="tf-alerts-chips">
          {TF_ROOTS.map((r) => (
            <label key={r} className="tf-alerts-tol-item">
              {r}
              <input
                type="number" min={0} max={50} step={0.25} value={s.levelTolPts[r] ?? 0}
                onChange={(e) => edit({ levelTolPts: { ...s.levelTolPts, [r]: num(e.target.value, s.levelTolPts[r] ?? 0) } })}
                data-testid={`tradefinder-alerts-tol-${r}`}
              />
            </label>
          ))}
        </span>
      </div>
      <label className="tf-ux-row">
        <span>Min size (|value|, 0 = any)</span>
        <input
          type="number" min={0} step={10} value={s.minValue}
          onChange={(e) => edit({ minValue: num(e.target.value, s.minValue) })}
          data-testid="tradefinder-alerts-min-value"
        />
      </label>
      <label className="tf-ux-row">
        <span>Cooldown per kind + symbol (min)</span>
        <input
          type="number" min={0} max={1440} step={1} value={Math.round(s.cooldownSec / 60)}
          onChange={(e) => edit({ cooldownSec: Math.round(num(e.target.value, s.cooldownSec / 60) * 60) })}
          data-testid="tradefinder-alerts-cooldown"
        />
      </label>
      <label className="tf-ux-row">
        <span>Max alerts per minute</span>
        <input
          type="number" min={1} max={60} step={1} value={s.maxPerMinute}
          onChange={(e) => edit({ maxPerMinute: num(e.target.value, s.maxPerMinute) })}
          data-testid="tradefinder-alerts-max-per-min"
        />
      </label>
      <Switch
        on={s.quietHours.enabled}
        label="Quiet hours"
        description={`No pushes from ${s.quietHours.start} to ${s.quietHours.end} (${s.quietHours.tz})`}
        glyph="☾"
        testId="tradefinder-alerts-quiet"
        onChange={(on) => edit({ quietHours: { ...s.quietHours, enabled: on } })}
      />
      <div className="tf-ux-row">
        <span>Quiet from / to</span>
        <span className="tf-alerts-chips">
          <input
            type="time" value={s.quietHours.start}
            onChange={(e) => edit({ quietHours: { ...s.quietHours, start: e.target.value } })}
            data-testid="tradefinder-alerts-quiet-start"
          />
          <input
            type="time" value={s.quietHours.end}
            onChange={(e) => edit({ quietHours: { ...s.quietHours, end: e.target.value } })}
            data-testid="tradefinder-alerts-quiet-end"
          />
        </span>
      </div>
      <div className="tf-alerts-actions">
        <button
          type="button"
          className="tf-alerts-save"
          disabled={saving || !dirty}
          onClick={() => void save()}
          data-testid="tradefinder-alerts-save"
        >
          {saving ? 'Saving…' : 'Save alerts'}
        </button>
        {draft !== null && (
          <button type="button" className="tf-alerts-revert" onClick={() => { setDraft(null); setMsg(null); }} data-testid="tradefinder-alerts-revert">
            Revert
          </button>
        )}
        {msg && (
          <span className={msg.ok ? 'tf-alerts-ok' : 'tf-alerts-err'} data-testid="tradefinder-alerts-msg">{msg.text}</span>
        )}
      </div>
    </div>
    );
  }
};

export default AlertsSection;
